// Authentication Context Class Reference (ACR) values requested via OIDC
// `acr_values` and asserted in the access/id token `acr` claim. SINGLE_FACTOR
// is one factor (author password or reviewer email passcode); MFA is any
// multi-factor. This is the cross-cutting contract the API enforcement gate and
// the web step-up gate both key off — keep it here so they cannot drift.
export const ACR_VALUES = Object.freeze({
  SINGLE_FACTOR: 'urn:agilemd:loa:1fa:pwd',
  MFA: 'urn:agilemd:loa:mfa:any'
} as const);

export type AcrValue = (typeof ACR_VALUES)[keyof typeof ACR_VALUES];

// Numeric strength ordering used to decide whether a presented `acr` meets a
// route's required level. Higher satisfies lower (MFA satisfies SINGLE_FACTOR).
// Keycloak stamps its NUMERIC Level-of-Authentication as the acr when a flow
// completes without a named acr mapping (e.g. the registration flow): 1 = one
// factor, 2 = multi-factor — alias those to the named levels.
export const ACR_LOA_MAP = Object.freeze({
  [ACR_VALUES.SINGLE_FACTOR]: 10,
  [ACR_VALUES.MFA]: 20,
  '1': 10,
  '2': 20
} as const);

// Purpose claim stamped into invite JWTs (reviewer and team/role) and required
// wherever a token is accepted as proof of inbox control (the D1a instant
// connect). Several other token families are signed with the same shared
// secret; without the claim, any of them that ever grows an `email` field
// would silently become email-confirmation currency.
export const INVITE_TOKEN_TYPE = 'invite';

// What an invited address resolves to when an unauthenticated invitee opens the
// link, and therefore where the landing sends them. A CONFIRMED_OWNER and an
// UNPROVEN_HOLDER both already have an account to sign into; only NONE is
// genuinely new. Disclosed only against a valid invite token (inbox proof).
export const INVITED_EMAIL_STATES = Object.freeze({
  CONFIRMED_OWNER: 'confirmed_owner',
  UNPROVEN_HOLDER: 'unproven_holder',
  NONE: 'none'
} as const);

export type InvitedEmailState = (typeof INVITED_EMAIL_STATES)[keyof typeof INVITED_EMAIL_STATES];
